import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoutePageView from "@/components/RoutePageView";
import PublicLayout from "@/components/layout/PublicLayout";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Checkout from "@/pages/Checkout";
import OrderTracking from "@/pages/OrderTracking";
import Studio from "@/pages/Studio";
import Contact from "@/pages/Contact";
import Founders from "@/pages/Founders";
import CodConfirm from "@/pages/CodConfirm";
import Reviews from "@/pages/Reviews";
import HowItWorks from "@/pages/HowItWorks";
import Gallery from "@/pages/Gallery";
import FAQs from "@/pages/FAQs";
import CustomerDashboard from "@/pages/CustomerDashboard";
import CreateMiniature from "@/pages/CreateMiniature";
import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/Products";
import AdminOrders from "@/pages/admin/Orders";
import AdminReviews from "@/pages/admin/Reviews";
import AdminGallery from "@/pages/admin/Gallery";
import AdminEmail from "@/pages/admin/Email";
import AdminTeam from "@/pages/admin/Team";
import TeamLayout from "@/pages/team/TeamLayout";
import TeamOverview from "@/pages/team/TeamOverview";
import TaskBoard from "@/pages/team/TaskBoard";
import SalesDashboard from "@/pages/team/SalesDashboard";
import SupportDashboard from "@/pages/team/SupportDashboard";
import { TeamOrders, TeamProducts, TeamLeads } from "@/pages/team/TeamReadOnly";
import AdminLeads from "@/pages/admin/Leads";
import AdminSettings from "@/pages/admin/Settings";
import AdminMiniatures from "@/pages/admin/Miniatures";
import AdminMiniatureActivity from "@/pages/admin/MiniatureActivity";

const Wrap = ({ children }) => <PublicLayout>{children}</PublicLayout>;

function App() {
    return (
        <BrowserRouter>
            <RoutePageView />
            <AuthProvider>
                <SettingsProvider>
                    <CartProvider>
                        <Toaster
                            position="top-center"
                            toastOptions={{
                                classNames: {
                                    toast: "!rounded-2xl !glass-strong !border-white/60",
                                },
                            }}
                        />
                        <Routes>
                            <Route path="/" element={<Wrap><Home /></Wrap>} />
                            <Route path="/shop" element={<Wrap><Shop /></Wrap>} />
                            <Route path="/product/:slug" element={<Wrap><ProductDetail /></Wrap>} />
                            <Route path="/how-it-works" element={<Wrap><HowItWorks /></Wrap>} />
                            <Route path="/gallery" element={<Wrap><Gallery /></Wrap>} />
                            <Route path="/reviews" element={<Wrap><Reviews /></Wrap>} />
                            <Route path="/faqs" element={<Wrap><FAQs /></Wrap>} />
                            <Route path="/track" element={<Wrap><OrderTracking /></Wrap>} />
                            <Route path="/studio" element={<Wrap><Studio /></Wrap>} />
                            <Route path="/contact" element={<Wrap><Contact /></Wrap>} />
                            <Route path="/founders" element={<Wrap><Founders /></Wrap>} />
                            <Route path="/cod/confirm" element={<Wrap><CodConfirm /></Wrap>} />
                            <Route path="/create" element={<Wrap><CreateMiniature /></Wrap>} />

                            {/* Auth — kept for admin/team access only */}
                            <Route path="/login" element={<Wrap><Login /></Wrap>} />
                            <Route path="/forgot-password" element={<Wrap><ForgotPassword /></Wrap>} />
                            <Route path="/reset-password" element={<Wrap><ResetPassword /></Wrap>} />

                            {/* Checkout — guest checkout allowed */}
                            <Route path="/checkout" element={<Wrap><Checkout /></Wrap>} />

                            {/* Customer dashboard */}
                            <Route path="/dashboard/*" element={<ProtectedRoute><Wrap><CustomerDashboard /></Wrap></ProtectedRoute>} />

                            {/* Admin */}
                            <Route path="/admin" element={<ProtectedRoute roles={["admin", "super_admin"]}><AdminLayout /></ProtectedRoute>}>
                                <Route index element={<AdminDashboard />} />
                                <Route path="products" element={<AdminProducts />} />
                                <Route path="orders" element={<AdminOrders />} />
                                <Route path="reviews" element={<AdminReviews />} />
                                <Route path="gallery" element={<AdminGallery />} />
                                <Route path="leads" element={<AdminLeads />} />
                                <Route path="email" element={<AdminEmail />} />
                                <Route path="team" element={<AdminTeam />} />
                                <Route path="settings" element={<AdminSettings />} />
                                <Route path="miniatures" element={<AdminMiniatures />} />
                                <Route path="miniature-activity" element={<AdminMiniatureActivity />} />
                            </Route>

                            {/* Team portal */}
                            <Route path="/team" element={<ProtectedRoute roles={["sales", "support", "designer", "production", "painting", "packing", "finance", "operations", "admin", "super_admin"]}><TeamLayout /></ProtectedRoute>}>
                                <Route index element={<TeamOverview />} />
                                <Route path="tasks" element={<TaskBoard />} />
                                <Route path="orders" element={<TeamOrders />} />
                                <Route path="products" element={<TeamProducts />} />
                                <Route path="leads" element={<TeamLeads />} />
                                <Route path="sales" element={<SalesDashboard />} />
                                <Route path="support" element={<SupportDashboard />} />
                            </Route>

                            <Route path="*" element={<Navigate to="/" replace />} />
                        </Routes>
                    </CartProvider>
                </SettingsProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
